import {HubspotForm, HubspotFormLocale} from 'src/common/types'

export const locales = {
  'en-gb': 'en-gb',
  'en-us': 'en-us',
  'de-de': 'de-de',
  'de-at': 'de-at',
  'nl-nl': 'nl-nl',
} as const

export type LocalesType = (typeof locales)[keyof typeof locales]

export const DEFAULT_LOCALE = locales['en-us']

export const GET_MOSS_HOSTNAME = 'https://www.getmoss.com'

export const TAG_COLORS = {
  orange: {
    backgroundColor: '#FDF3ED',
    color: '#B95E09',
  },
  blue: {
    backgroundColor: '#E8EDFD',
    color: '#4C6EE5',
  },
  green: {
    backgroundColor: '#E6EED3',
    color: '#5B820D',
  },
  red: {
    backgroundColor: '#F9E8E7',
    color: '#CB4C43',
  },
  yellow: {
    backgroundColor: '#F3EAD3',
    color: '#947100',
  },
  mossGreen: {
    backgroundColor: '#E6EFEC',
    color: '#4E7E6C',
  },
  pink: {
    backgroundColor: '#F6E5EB',
    color: '#BA4F7A',
  },
  purple: {
    backgroundColor: '#EBE7F9',
    color: '#8064CE',
  },
  grey: {
    backgroundColor: '#F1F1F1',
    color: '#72716E',
  },
  'brand-purple': {
    backgroundColor: '#6C69FC',
    color: '#FFFFFF',
  },
  'neon-green': {
    backgroundColor: '#B9E97D',
    color: '#232F2A',
  },
  white: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: '#FFFFFF',
    backdropFilter: 'blur(10px)',
  },
  'green-reskin': {
    backgroundColor: '#6BBC00',
    color: '#FFF',
  },
} as const

export const PRODUCT_ANNOUNCEMENTS_CATEGORIES = {
  [locales['en-gb']]: {
    ALL: 'All',
    CARDS: 'Cards',
    ACCOUNTS_PAYABLE: 'Accounts Payable',
    REIMBURSEMENTS: 'Reimbursements',
    PAYMENTS: 'Payments',
    ACCOUNTING: 'Accounting',
    CONTROLS_AND_TEAM: 'Controls and Team',
    GENERAL: 'General',
  },
  [locales['en-us']]: {
    ALL: 'All',
    CARDS: 'Cards',
    ACCOUNTS_PAYABLE: 'Accounts Payable',
    REIMBURSEMENTS: 'Reimbursements',
    PAYMENTS: 'Payments',
    ACCOUNTING: 'Accounting',
    CONTROLS_AND_TEAM: 'Controls and Team',
    GENERAL: 'General',
  },
  [locales['de-de']]: {
    ALL: 'Alle',
    CARDS: 'Karten',
    ACCOUNTS_PAYABLE: 'Accounts Payable',
    REIMBURSEMENTS: 'Rückerstattungen',
    PAYMENTS: 'Zahlungen',
    ACCOUNTING: 'Buchhaltung',
    CONTROLS_AND_TEAM: 'Kontrolle & Teams',
    GENERAL: 'Allgemein',
  },
  [locales['de-at']]: {
    ALL: 'Alle',
    CARDS: 'Karten',
    ACCOUNTS_PAYABLE: 'Accounts Payable',
    REIMBURSEMENTS: 'Rückerstattungen',
    PAYMENTS: 'Zahlungen',
    ACCOUNTING: 'Buchhaltung',
    CONTROLS_AND_TEAM: 'Kontrolle & Teams',
    GENERAL: 'Allgemein',
  },
  [locales['nl-nl']]: {
    ALL: 'Alle',
    CARDS: 'Betaalkaarten',
    ACCOUNTS_PAYABLE: 'Crediteurenbeheer',
    REIMBURSEMENTS: 'Declaraties',
    PAYMENTS: 'Betalingen',
    ACCOUNTING: 'Boekhouding',
    CONTROLS_AND_TEAM: 'Controle & teams',
    GENERAL: 'Algemeen',
  },
} as const

export const CATEGORIES_COLORS = {
  CARDS: 'blue',
  ACCOUNTS_PAYABLE: 'green',
  REIMBURSEMENTS: 'pink',
  PAYMENTS: 'purple',
  ACCOUNTING: 'orange',
  CONTROLS_AND_TEAM: 'yellow',
  GENERAL: 'mossGreen',
} as const

export const NAVIGATION_QUERY = `{
    navigation {
      ...navigationFields
      menu_links {
        link_label
        link_url
        marketingid
        submenu {
          ... on submenu {
            slices
          }
        }
      }
    }
  }`

export const OPACITY_0 = {
  opacity: 0,
}

export const OPACITY_1 = {
  opacity: 1,
}

export const OPACITY_0_DURATION_0_5 = {
  opacity: 0,
  transition: {duration: 0.5},
}

export const DEFAULT_PRODUCT_TYPE = 'WALLET'
export const DEFAULT_MARKET = 'DE'
export const DEFAULT_SEGMENT_TYPE = 'STARTUP'
export const UTM_PARAMS_KEY = 'utm-params'

export const marketingConsentUrl =
  'https://getmoss.com/public/terms-and-conditions/20230803_Nufin_GmbH_User_Marketing_Consent_v1_EN.pdf'

export type Locales = 'en-gb' | 'en-us' | 'de-de' | 'nl-nl' | 'de-at'

export const CATEGORIES = {
  All: {
    'en-gb': 'All',
    'en-us': 'All',
    'de-de': 'Alle',
    'nl-nl': 'Alle',
    'de-at': 'Alle',
  },
  Ecommerce: {
    'en-gb': 'Ecommerce',
    'en-us': 'Ecommerce',
    'de-de': 'E-commerce',
    'nl-nl': 'E-commerce',
    'de-at': 'E-Commerce',
  },
  'Professional Services': {
    'en-gb': 'Professional Services',
    'en-us': 'Professional Services',
    'de-de': 'Dienstleistungen',
    'nl-nl': 'Professionele dienstverlening',
    'de-at': 'Dienstleistungen',
  },
  Hospitality: {
    'en-gb': 'Hospitality',
    'en-us': 'Hospitality',
    'de-de': 'Gastgewerbe',
    'nl-nl': 'Hospitality',
    'de-at': 'Gastgewerbe',
  },
  Technology: {
    'en-gb': 'Technology',
    'en-us': 'Technology',
    'de-de': 'Technologie',
    'nl-nl': 'Technologie',
    'de-at': 'Technologie',
  },
}

export const REVIEW_PLATFORMS = {
  G2: {
    'en-us': 'G2',
    'en-gb': 'G2',
    'de-de': 'G2',
    'nl-nl': 'G2',
    'de-at': 'G2',
  },
  Capterra: {
    'en-us': 'Capterra',
    'en-gb': 'Capterra',
    'de-de': 'Capterra',
    'nl-nl': 'Capterra',
    'de-at': 'Capterra',
  },
  'Datev marketplace': {
    'en-us': 'DATEV Marketplace',
    'en-gb': 'DATEV Marketplace',
    'de-de': 'DATEV-Marktplatz',
    'nl-nl': 'DATEV-Marktplatz',
    'de-at': 'DATEV-Marktplatz',
  },
}

export const LOCATION_TRANSLATIONS = {
  'en-us': 'Location',
  'en-gb': 'Location',
  'de-de': 'Standort',
  'nl-nl': 'Locatie',
  'de-at': 'Standort',
} as const

export const BASE_URL = 'https://www.getmoss.com'

export const BREAKPOINTS = {
  TABLET: 768,
  DESKTOP: 1024,
}

export const DARK_BACKGROUND_COLORS = ['Dark Green']

export const DREAM_DATA_KEY = 'a0980a5b-52fe-4e04-beed-2e8166512969'
export const DREAM_DATA_SERVICE_ID = 'jE46pSyKb4wx8z'
export const DREAM_DATA_SERVICE_NAME = 'Dreamdata'
export const USERLED_SERVICE_ID = 'jCb-yiiH6wjwWi'

export const LEAD_ID_STORAGE_KEY = 'funnel-lead-id'
export const MOSS_DOMAIN = 'getmoss.com'

export const MKT_CONSENT = {
  en: {
    id: '119bf461-2238-4014-bdbe-9cd212ef8888',
    url: 'https://getmoss.com/public/terms-and-conditions/20240701_INT-en_User_Marketing_Consent_v2.0.pdf',
  },
  de: {
    id: '119bf461-2238-4014-bdbe-9cd212ef8888',
    url: 'https://getmoss.com/public/terms-and-conditions/20240701_INT-de_User_Marketing_Consent_v2.0.pdf',
  },
  nl: {
    id: '119bf461-2238-4014-bdbe-9cd212ef8888',
    url: 'https://getmoss.com/public/terms-and-conditions/20240701_INT-en_User_Marketing_Consent_v2.0.pdf',
  },
} as const

export const PRIVACY_CONSENT = {
  en: {
    id: 'f6cb29ed-32de-422a-abd4-02d231fc8a71',
    url: 'https://getmoss.com/public/terms-and-conditions/20240701_INT-en_Nufin_GmbH_DPP_v3.0.pdf',
  },
  de: {
    id: 'f6cb29ed-32de-422a-abd4-02d231fc8a71',
    url: 'https://getmoss.com/public/terms-and-conditions/20240701_INT-de_Nufin_GmbH_DPP_v3.0.pdf',
  },
  nl: {
    id: 'f6cb29ed-32de-422a-abd4-02d231fc8a71',
    url: 'https://getmoss.com/public/terms-and-conditions/20240701_INT-en_Nufin_GmbH_DPP_v3.0.pdf',
  },
} as const

export const DEVICE_ID_COOKIE_NAME = 'ms_client_device_id'
export const CONVERT_COOKIE_NAME = '_conv_v'

export const HUBSPOT_LOCALE_FORM_MAP: Record<HubspotFormLocale, HubspotForm> = {
  nl: {
    formId: 'e4f8066a-f64c-495d-9a6e-4ae6dedc9838',
    portalId: '25068079',
  },
  de: {
    formId: '307cfbf6-8c43-4d54-b1fd-8bc77b0742da',
    portalId: '25068079',
  },
  en: {
    formId: '41cc3f8b-d0f6-416e-9496-5f1841231b0c',
    portalId: '25068079',
  },
}
